import {
  NOTIFICATION_ACTION_LABEL_MAX,
  NOTIFICATION_BODY_MAX,
  NOTIFICATION_ICONS,
  NOTIFICATION_TITLE_MAX,
  type NotificationCategory,
  type NotificationIcon,
  type NotificationRenderContext,
} from "@loot-os/api-contract";
import { Button } from "@loot-os/ui/components/button";
import {
  Field,
  FieldContent,
  FieldDescription,
  FieldError,
  FieldLabel,
} from "@loot-os/ui/components/field";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupText,
  InputGroupTextarea,
} from "@loot-os/ui/components/input-group";
import { Popover, PopoverContent, PopoverTrigger } from "@loot-os/ui/components/popover";
import { Switch } from "@loot-os/ui/components/switch";
import {
  NOTIFICATION_CATEGORY_TILE,
  NOTIFICATION_ICON_COMPONENTS,
} from "@loot-os/ui/lib/notifications";
import { cn } from "@loot-os/ui/lib/utils";
import { ChevronsUpDown } from "lucide-react";
import { type Dispatch, type SetStateAction, useRef, useState } from "react";

import {
  boxPageLink,
  insertPlaceholder,
  linkDraftTo,
  type NotificationDraft,
  type NotificationDraftField,
  type NotificationDraftIssues,
  notificationIconName,
  type PlaceholderChip,
  placeholderChips,
  storefrontPageLinks,
} from "../lib/notifications";
import { useNotificationImageUpload } from "../lib/useNotifications";
import { NotificationBoxPicker } from "./NotificationBoxPicker";
import { RasterImageField } from "./StorefrontFields";

/** The text fields a placeholder chip can go into. */
type TextField = "title" | "body" | "actionLabel" | "actionUrl";

/**
 * A notification's content as both editors take it: title and message with placeholder chips,
 * the icon, an optional button and an optional banner. Placeholders a type does not have are
 * named under the field; `showAllIssues` adds the empty-field ones, after a save was tried.
 */
export function NotificationContentFields({
  id,
  draft,
  setDraft,
  issues,
  showAllIssues,
  variables,
  context,
  category,
  disabled,
  uploadsAvailable,
}: {
  id: string;
  draft: NotificationDraft;
  setDraft: Dispatch<SetStateAction<NotificationDraft>>;
  issues: NotificationDraftIssues;
  showAllIssues: boolean;
  /** The type's own placeholders; none for a marketing notification. */
  variables: readonly { name: string; label: string }[];
  context: NotificationRenderContext;
  category: NotificationCategory;
  disabled: boolean;
  uploadsAvailable: boolean;
}) {
  const fields = useRef<Partial<Record<TextField, HTMLInputElement | HTMLTextAreaElement | null>>>(
    {},
  );
  // Where a chip goes: the text field typed in last, the message until one is.
  const lastFocused = useRef<TextField>("body");
  const { upload, uploading } = useNotificationImageUpload();

  const issueFor = (field: NotificationDraftField, value: string) =>
    showAllIssues || value.trim() !== "" ? issues[field] : undefined;

  const textProps = (field: TextField) => ({
    id: `${id}-${field}`,
    value: draft[field],
    disabled,
    "aria-invalid": issueFor(field, draft[field]) ? true : undefined,
    ref: (element: HTMLInputElement | HTMLTextAreaElement | null) => {
      fields.current[field] = element;
    },
    onFocus: () => {
      lastFocused.current = field;
    },
    onChange: (e: { target: { value: string } }) => {
      const { value } = e.target;
      setDraft((current) => ({ ...current, [field]: value }));
    },
  });

  const insert = (token: string) => {
    const typedIn = lastFocused.current;
    const field = !draft.linked && typedIn !== "title" && typedIn !== "body" ? "body" : typedIn;
    const element = fields.current[field];
    const selection =
      element && element.selectionStart !== null
        ? { start: element.selectionStart, end: element.selectionEnd ?? element.selectionStart }
        : null;
    const next = insertPlaceholder(draft[field], selection, token);
    setDraft((current) => ({ ...current, [field]: next.value }));
    requestAnimationFrame(() => {
      element?.focus();
      element?.setSelectionRange(next.caret, next.caret);
    });
  };

  const chips = placeholderChips(variables, context);
  const titleIssue = issueFor("title", draft.title);
  const bodyIssue = issueFor("body", draft.body);
  const labelIssue = issueFor("actionLabel", draft.actionLabel);
  const urlIssue = issueFor("actionUrl", draft.actionUrl);

  return (
    <div className="grid gap-6">
      <Field data-invalid={titleIssue ? true : undefined}>
        <FieldLabel htmlFor={`${id}-title`}>Title</FieldLabel>
        <InputGroup>
          <InputGroupInput {...textProps("title")} />
          <InputGroupAddon align="inline-end">
            <InputGroupText className="text-xs tabular-nums">
              {draft.title.trim().length}/{NOTIFICATION_TITLE_MAX}
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        {titleIssue && <FieldError>{titleIssue}</FieldError>}
      </Field>

      <Field data-invalid={bodyIssue ? true : undefined}>
        <FieldLabel htmlFor={`${id}-body`}>Message</FieldLabel>
        <InputGroup>
          <InputGroupTextarea rows={3} className="min-h-20" {...textProps("body")} />
          <InputGroupAddon align="block-end">
            <InputGroupText className="ml-auto text-xs tabular-nums">
              {draft.body.trim().length}/{NOTIFICATION_BODY_MAX}
            </InputGroupText>
          </InputGroupAddon>
        </InputGroup>
        {bodyIssue && <FieldError>{bodyIssue}</FieldError>}
      </Field>

      <div className="grid gap-3">
        <div className="grid gap-1">
          <p className="text-sm font-medium">Placeholders</p>
          <p className="text-sm text-muted-foreground">
            Click one to put it where you were typing; it fills in when the notification is sent.
            Capitalize one ({"{Box}"}) to start a sentence with it. Text in [brackets] is left out
            when a placeholder inside it is empty.
          </p>
        </div>
        {chips.details.length > 0 && (
          <PlaceholderChips
            label="This notification"
            chips={chips.details}
            disabled={disabled}
            onInsert={insert}
          />
        )}
        <PlaceholderChips
          label="Store and words"
          chips={chips.words}
          disabled={disabled}
          onInsert={insert}
        />
      </div>

      <Field>
        <FieldLabel htmlFor={`${id}-icon`}>Icon</FieldLabel>
        <IconPicker
          id={`${id}-icon`}
          value={draft.icon}
          category={category}
          disabled={disabled}
          onChange={(icon) => setDraft((current) => ({ ...current, icon }))}
        />
      </Field>

      <div className="grid gap-4">
        <Field orientation="horizontal">
          <Switch
            id={`${id}-linked`}
            checked={draft.linked}
            disabled={disabled}
            onCheckedChange={(linked) => setDraft((current) => ({ ...current, linked }))}
          />
          <FieldContent>
            <FieldLabel htmlFor={`${id}-linked`}>Button</FieldLabel>
            <FieldDescription>
              A link under the message, to a page of the store or an https:// site.
            </FieldDescription>
          </FieldContent>
        </Field>
        {draft.linked && (
          <div className="grid gap-4 rounded-lg border p-4">
            <div className="grid gap-4 sm:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]">
              <Field data-invalid={labelIssue ? true : undefined}>
                <FieldLabel htmlFor={`${id}-actionLabel`}>Label</FieldLabel>
                <InputGroup>
                  <InputGroupInput placeholder="View {inventory}" {...textProps("actionLabel")} />
                  <InputGroupAddon align="inline-end">
                    <InputGroupText className="text-xs tabular-nums">
                      {draft.actionLabel.trim().length}/{NOTIFICATION_ACTION_LABEL_MAX}
                    </InputGroupText>
                  </InputGroupAddon>
                </InputGroup>
                {labelIssue && <FieldError>{labelIssue}</FieldError>}
              </Field>
              <Field data-invalid={urlIssue ? true : undefined}>
                <FieldLabel htmlFor={`${id}-actionUrl`}>Link</FieldLabel>
                <InputGroup>
                  <InputGroupInput placeholder="/boxes" {...textProps("actionUrl")} />
                </InputGroup>
                {urlIssue && <FieldError>{urlIssue}</FieldError>}
              </Field>
            </div>
            <div className="flex flex-wrap items-center gap-1.5">
              <span className="text-xs text-muted-foreground">Link to</span>
              {storefrontPageLinks(context.terms).map((page) => (
                <Button
                  key={page.url}
                  type="button"
                  variant="outline"
                  size="xs"
                  disabled={disabled}
                  aria-pressed={draft.actionUrl.trim() === page.url}
                  onClick={() => setDraft((current) => linkDraftTo(current, page))}
                >
                  {page.label}
                </Button>
              ))}
              <div className="w-48">
                <NotificationBoxPicker
                  disabled={disabled}
                  onPick={(box) => setDraft((current) => linkDraftTo(current, boxPageLink(box.id)))}
                />
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="grid gap-2">
        <RasterImageField
          id={`${id}-image`}
          label="Banner image"
          url={draft.imageUrl}
          onUrlChange={(imageUrl) => setDraft((current) => ({ ...current, imageUrl }))}
          onFile={async (file) => {
            const imageUrl = await upload(file);
            setDraft((current) => ({ ...current, imageUrl }));
          }}
          uploading={uploading}
          disabled={disabled}
          uploadsAvailable={uploadsAvailable}
          previewClassName="size-full object-cover"
          help="Optional. Shown across the notification at 3:1. PNG, JPEG, WEBP or AVIF, 5 MB at most."
        />
        {issues.imageUrl && <FieldError>{issues.imageUrl}</FieldError>}
        {draft.imageUrl && !disabled && (
          <div>
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={() => setDraft((current) => ({ ...current, imageUrl: "" }))}
            >
              Remove banner
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

/** One row of placeholder chips; a chip keeps the caret in the field it inserts into. */
function PlaceholderChips({
  label,
  chips,
  disabled,
  onInsert,
}: {
  label: string;
  chips: PlaceholderChip[];
  disabled: boolean;
  onInsert: (token: string) => void;
}) {
  return (
    <div className="grid gap-1.5">
      <span className="text-xs text-muted-foreground">{label}</span>
      <div className="flex flex-wrap gap-1.5">
        {chips.map((chip) => (
          <Button
            key={chip.token}
            type="button"
            variant="outline"
            size="xs"
            disabled={disabled}
            title={chip.hint}
            className="max-w-full"
            // Keeps focus, and so the selection, in the text field.
            onMouseDown={(e) => e.preventDefault()}
            onClick={() => onInsert(chip.token)}
          >
            <span className="font-mono">{`{${chip.token}}`}</span>
            <span className="truncate font-normal text-muted-foreground">{chip.hint}</span>
          </Button>
        ))}
      </div>
    </div>
  );
}

/** The curated icons in a popover grid, on the category's tile like the storefront draws it. */
function IconPicker({
  id,
  value,
  category,
  disabled,
  onChange,
}: {
  id: string;
  value: NotificationIcon;
  category: NotificationCategory;
  disabled: boolean;
  onChange: (icon: NotificationIcon) => void;
}) {
  const [open, setOpen] = useState(false);
  const Current = NOTIFICATION_ICON_COMPONENTS[value];
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger
        render={
          <Button
            id={id}
            type="button"
            variant="outline"
            disabled={disabled}
            className="h-auto w-fit justify-start gap-2 py-1.5 pr-2 pl-1.5"
          />
        }
      >
        <span
          className={cn(
            "flex size-7 items-center justify-center rounded-md",
            NOTIFICATION_CATEGORY_TILE[category],
          )}
        >
          <Current aria-hidden />
        </span>
        <span className="capitalize">{notificationIconName(value)}</span>
        <ChevronsUpDown className="size-3.5 opacity-60" />
      </PopoverTrigger>
      <PopoverContent align="start" className="w-auto p-2">
        <div className="grid grid-cols-6 gap-1">
          {NOTIFICATION_ICONS.map((icon) => {
            const Icon = NOTIFICATION_ICON_COMPONENTS[icon];
            const name = notificationIconName(icon);
            return (
              <Button
                key={icon}
                type="button"
                variant={icon === value ? "secondary" : "ghost"}
                size="icon"
                aria-label={name}
                aria-pressed={icon === value}
                title={name}
                onClick={() => {
                  onChange(icon);
                  setOpen(false);
                }}
              >
                <Icon />
              </Button>
            );
          })}
        </div>
      </PopoverContent>
    </Popover>
  );
}
